import { getSectionsMap } from '../store';
import {
    getSectionsLastError,
    loadSections,
    loadSectionsInProgress,
} from 'places-analytics-app-store-operations/lib/operations/loadSections';
import { useCallback, useEffect, useMemo } from 'react';
import type { ApolloError } from '@apollo/client';
import type { Place } from 'owa-graph-schema';

const EMPTY_ARRAY: never[] = [];

/**
 * This function retrieves the sections data from the store.
 * It is used to provide a consistent interface for accessing this data,
 * including the loading state and any errors that may have occurred from the operations module.
 */
export function useSectionsFromStore(
    floorId: string,
    skip = false
): {
    loading: boolean;
    error: ApolloError | undefined;
    sections: Place[];
    refetch: (() => void) | undefined;
} {
    const currentSections = getSectionsMap();
    const refetch = useCallback((): void => {
        if (!skip && floorId && !loadSectionsInProgress(floorId)) {
            loadSections(floorId, true /* refetch */);
        }
    }, [skip, floorId]);

    const loading = skip ? false : loadSectionsInProgress(floorId);
    const error = skip ? undefined : (getSectionsLastError(floorId) as unknown as ApolloError);
    const sections = skip ? EMPTY_ARRAY : currentSections.get(floorId) || EMPTY_ARRAY;

    // If data is missing and not already loading, initiate a load
    useEffect(() => {
        if (!skip && !currentSections.has(floorId) && !loading && !error) {
            loadSections(floorId);
        }
    }, [skip, currentSections, floorId, loading, error]);

    return {
        loading,
        error,
        sections,
        refetch: skip ? undefined : refetch,
    };
}

export function useSectionSelector(
    floorId: string,
    skip = false
): {
    loading: boolean;
    error: ApolloError | undefined;
    sections: Place[];
    refetch: (() => void) | undefined;
} {
    const sectionsData = useSectionsFromStore(floorId, skip);
    return useMemo(() => sectionsData, [sectionsData]);
}
